// Mobile app phone line (iOS / Android only). The voice agent keeps the
// user's extension registered and relays incoming calls over a websocket
// (backend/voice_agent/mobile.py); the app answers with its own WebRTC SDP,
// so the audio then flows directly between the phone and the PBX.
//
// This module registers the phone (PUT /api/telephony/mobile/device) and
// keeps the gateway connection open: reconnect with backoff, again when the
// app comes back to the foreground, renewing the JWT with the device key.
import { isNativeApp, nativePlatform, onAppResume } from './native';
import { ensureFreshSession, renewWithDeviceKey } from './deviceSession';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const INSTALL_KEY = 'runvya.installId';

export const gatewayUrl = () => `${API_URL.replace(/^http/, 'ws')}/api/voice/ws`;

// A stable id for this app install (one phone = one row in mobile_devices).
export function installId() {
  let id = null;
  try {
    id = localStorage.getItem(INSTALL_KEY);
  } catch { /* storage unavailable */ }
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `${Date.now()}${Math.random()}`).replace(/[^A-Za-z0-9]/g, '').slice(0, 32);
    try {
      localStorage.setItem(INSTALL_KEY, id);
    } catch { /* storage unavailable */ }
  }
  return id;
}

async function api(method, path, body, givenToken) {
  const token = givenToken || await ensureFreshSession();
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// "Ring this phone for my extension." Extra fields (push tokens) are merged in.
export function registerPhone(extra = {}) {
  if (!isNativeApp()) return Promise.resolve(null);
  return api('PUT', '/api/telephony/mobile/device', {
    install_id: installId(), platform: nativePlatform(), ring: true, ...extra,
  });
}

// Logout on the phone: stop ringing it. Best effort; pass the token, the
// caller is about to clear it.
export async function unregisterPhone(token) {
  if (!isNativeApp() || !token) return;
  try {
    await api('DELETE', `/api/telephony/mobile/device/${installId()}`, undefined, token);
  } catch { /* offline: the line stops when the device key is revoked */ }
}

const BACKOFF_MS = [1000, 2000, 5000, 10000, 30000];

// Keep the gateway connection open. onMessage(msg) gets every server frame;
// onStatus('connecting' | 'ready' | 'offline'). Returns { send, close }.
export function connectMobileLine({ onMessage, onStatus, WebSocketImpl = window.WebSocket }) {
  let ws = null;
  let closed = false;
  let attempt = 0;
  let timer = null;

  const schedule = () => {
    if (closed || timer) return;
    const wait = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
    attempt += 1;
    timer = setTimeout(() => { timer = null; open(); }, wait);
  };

  async function open() {
    if (closed) return;
    onStatus?.('connecting');
    const token = await ensureFreshSession();
    if (closed) return;
    const socket = new WebSocketImpl(gatewayUrl());
    ws = socket;
    socket.onopen = () => socket.send(JSON.stringify({ type: 'auth', token }));
    socket.onmessage = (e) => {
      let msg;
      try {
        msg = JSON.parse(e.data);
      } catch {
        return;
      }
      if (msg.type === 'ready') {
        attempt = 0;
        onStatus?.('ready');
      }
      onMessage?.(msg);
    };
    socket.onclose = (e) => {
      if (ws === socket) ws = null;
      if (closed) return;
      onStatus?.('offline');
      // 4401: the JWT was refused (expired) -> renew it before retrying.
      if (e && e.code === 4401) renewWithDeviceKey().finally(schedule);
      else schedule();
    };
  }

  const stopResume = onAppResume(() => {
    if (!ws && !closed) {
      if (timer) { clearTimeout(timer); timer = null; }
      attempt = 0;
      open();
    }
  });
  open();

  return {
    send(msg) {
      if (ws && ws.readyState === 1) {
        ws.send(JSON.stringify(msg));
        return true;
      }
      return false;
    },
    close() {
      closed = true;
      if (timer) clearTimeout(timer);
      stopResume();
      if (ws) ws.close();
    },
  };
}

// Wait until ICE gathering is good enough to send the answer: a public
// (srflx) candidate, 1.5 s after the first candidate, or gathering complete
// (same rule as the browser phone).
export function waitForIce(pc, maxMs = 4000) {
  return new Promise((resolve) => {
    if (pc.iceGatheringState === 'complete') { resolve(); return; }
    let soon = null;
    const finish = () => {
      clearTimeout(soon);
      clearTimeout(hard);
      pc.removeEventListener('icecandidate', onCandidate);
      pc.removeEventListener('icegatheringstatechange', onState);
      resolve();
    };
    const onCandidate = (e) => {
      if (!e.candidate) { finish(); return; }
      if (/ typ srflx /.test(e.candidate.candidate || '')) { finish(); return; }
      if (!soon) soon = setTimeout(finish, 1500);
    };
    const onState = () => { if (pc.iceGatheringState === 'complete') finish(); };
    const hard = setTimeout(finish, maxMs);
    pc.addEventListener('icecandidate', onCandidate);
    pc.addEventListener('icegatheringstatechange', onState);
  });
}
